import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Dialog, Box, TextField, Typography, List, ListItemButton, ListItemIcon, ListItemText, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TodayIcon from '@mui/icons-material/Today';
import { ReactComponent as AddTaskSvg } from '../assets/images/AddTask.svg';
import { ReactComponent as FilterSvg } from '../assets/images/FilterWhite.svg';
import ProfileImage from '../assets/images/Profile_1.png';
import CustomizationImage from '../assets/images/Customization.png';
import ReportImage from '../assets/images/Report.png';
import LogOutImage from '../assets/images/Log out.png';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useThemeMode } from '../theme/ThemeContext';

const CommandPalette = () => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const navigate = useNavigate();
    const { themeMode, toggleThemeMode } = useThemeMode();
    const isDark = themeMode === 'dark';
    const inputRef = useRef(null);

    const PlatformSvgIcon = ({ Svg, size = 22 }) => (
        <Box sx={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { width: size, height: size, fill: isDark ? '#ccc' : '#666' }, '& svg path': { fill: isDark ? '#ccc' : '#666' } }}>
            <Svg />
        </Box>
    );
    const PlatformImgIcon = ({ src, size = 22 }) => (
        <Box component="img" src={src} sx={{ width: size, height: size, objectFit: 'contain', filter: isDark ? 'brightness(0) invert(0.8)' : 'none' }} />
    );

    const selectedOrganisation = useSelector((state) => state.organisation?.selectedOrganisation);
    const isAdmin = useMemo(() => {
        if (!selectedOrganisation) return false;
        const roles = [
            selectedOrganisation.role,
            selectedOrganisation.organisation_role,
            selectedOrganisation.user_role
        ].filter(Boolean).map((r) => String(r).toUpperCase());
        return roles.includes('ROLE_ADMIN');
    }, [selectedOrganisation]);

    // Listen for Cmd+K trigger
    useEffect(() => {
        const handler = () => {
            setOpen(true);
            setQuery('');
            setActiveIndex(0);
        };
        window.addEventListener('openCommandPalette', handler);
        return () => window.removeEventListener('openCommandPalette', handler);
    }, []);

    // Focus input when opened
    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [open]);

    const commands = useMemo(() => {
        const list = [
            {
                id: 'new-task',
                label: 'Create new task',
                shortcut: 'N',
                icon: <PlatformSvgIcon Svg={AddTaskSvg} />,
                keywords: ['new', 'task', 'add', 'create'],
                action: () => window.dispatchEvent(new CustomEvent('openAddTaskDialog', { detail: { pocket: null } }))
            },
            {
                id: 'task-templates',
                label: 'Use task template',
                icon: <BookmarkBorderIcon />,
                keywords: ['template', 'templates', 'reuse', 'saved'],
                action: () => window.dispatchEvent(new CustomEvent('openTaskTemplates', { detail: { mode: 'pick' } }))
            },
            {
                id: 'extract-from-notes',
                label: 'Extract tasks from meeting notes',
                icon: <AutoAwesomeIcon />,
                keywords: ['meeting', 'notes', 'transcript', 'extract', 'ai', 'action', 'items'],
                action: () => window.dispatchEvent(new CustomEvent('openMeetingNotesExtractor'))
            },
            {
                id: 'smart-suggest',
                label: 'Smart suggest priority & due date',
                icon: <AutoAwesomeIcon />,
                keywords: ['smart', 'suggest', 'ai', 'priority', 'due', 'date', 'auto'],
                action: () => window.dispatchEvent(new CustomEvent('suggestSmartTaskDefaults'))
            },
            {
                id: 'plan-my-day',
                label: 'Plan my day',
                icon: <TodayIcon />,
                keywords: ['plan', 'day', 'schedule', 'ai', 'daily', 'planner'],
                action: () => window.dispatchEvent(new CustomEvent('requestDailyPlanner'))
            },
            {
                id: 'go-profile',
                label: 'Go to Profile',
                icon: <PlatformImgIcon src={ProfileImage} />,
                keywords: ['profile', 'account', 'user'],
                action: () => navigate('/app/profile')
            },
            {
                id: 'go-settings',
                label: 'Go to Settings',
                icon: <PlatformImgIcon src={CustomizationImage} />,
                keywords: ['settings', 'preferences', 'config'],
                action: () => navigate('/app/settings')
            },
            {
                id: 'go-archives',
                label: 'Go to Archives',
                icon: <ArchiveOutlinedIcon />,
                keywords: ['archive', 'archived', 'old'],
                action: () => navigate('/app/archives')
            }
        ];

        // Admin-only: Reports
        if (isAdmin) {
            list.push({
                id: 'go-reports',
                label: 'Go to Reports',
                icon: <PlatformImgIcon src={ReportImage} />,
                keywords: ['reports', 'analytics', 'stats'],
                action: () => navigate('/app/report')
            });
        }

        list.push(
            {
                id: 'toggle-theme',
                label: isDark ? 'Switch to Light mode' : 'Switch to Dark mode',
                icon: isDark
                    ? <LightModeOutlinedIcon sx={{ color: '#f9a825' }} />
                    : <DarkModeOutlinedIcon sx={{ color: '#3fa3d0' }} />,
                keywords: ['theme', 'dark', 'light', 'mode', 'appearance'],
                action: () => toggleThemeMode()
            },
            {
                id: 'open-filters',
                label: 'Open Advanced Filters',
                icon: <PlatformSvgIcon Svg={FilterSvg} />,
                keywords: ['filters', 'filter', 'advanced'],
                action: () => window.dispatchEvent(new CustomEvent('openAdvancedFilters'))
            },
            {
                id: 'open-support',
                label: 'Send Support Request',
                icon: <HelpOutlineIcon />,
                keywords: ['support', 'help', 'feedback', 'bug'],
                action: () => window.dispatchEvent(new CustomEvent('openSupportDialog'))
            },
            {
                id: 'logout',
                label: 'Log out',
                icon: <PlatformImgIcon src={LogOutImage} />,
                keywords: ['logout', 'signout', 'exit'],
                action: () => { window.location.href = '/login'; }
            }
        );
        return list;
    }, [isDark, navigate, toggleThemeMode, isAdmin]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return commands;
        return commands.filter((c) => {
            if (c.label.toLowerCase().includes(q)) return true;
            return c.keywords.some((k) => k.includes(q));
        });
    }, [commands, query]);

    useEffect(() => {
        setActiveIndex(0);
    }, [query]);

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((prev) => Math.min(prev + 1, filtered.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((prev) => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const cmd = filtered[activeIndex];
            if (cmd) {
                cmd.action();
                setOpen(false);
            }
        }
    };

    const handleSelect = (cmd) => {
        cmd.action();
        setOpen(false);
    };

    return (
        <Dialog
            open={open}
            onClose={() => setOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    marginTop: '10vh',
                    alignSelf: 'flex-start',
                    overflow: 'hidden',
                    background: isDark ? '#1e1e1e' : '#ffffff'
                }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderBottom: `1px solid ${isDark ? '#333' : '#eee'}` }}>
                <SearchIcon sx={{ color: isDark ? '#888' : '#999' }} />
                <TextField
                    inputRef={inputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a command or search..."
                    fullWidth
                    variant="standard"
                    InputProps={{
                        disableUnderline: true,
                        sx: {
                            fontSize: '15px',
                            color: isDark ? '#e0e0e0' : '#1f2d3d'
                        }
                    }}
                />
            </Box>

            <List sx={{ maxHeight: '50vh', overflow: 'auto', py: 0.5 }}>
                {filtered.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: isDark ? '#888' : '#999' }}>
                            No results
                        </Typography>
                    </Box>
                ) : (
                    filtered.map((cmd, idx) => (
                        <ListItemButton
                            key={cmd.id}
                            selected={idx === activeIndex}
                            onClick={() => handleSelect(cmd)}
                            onMouseEnter={() => setActiveIndex(idx)}
                            sx={{
                                mx: 0.5,
                                borderRadius: '8px',
                                '&.Mui-selected': {
                                    backgroundColor: isDark ? 'rgba(39, 174, 134, 0.15)' : 'rgba(39, 174, 134, 0.08)'
                                },
                                '&.Mui-selected:hover': {
                                    backgroundColor: isDark ? 'rgba(39, 174, 134, 0.2)' : 'rgba(39, 174, 134, 0.12)'
                                }
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 36, color: isDark ? '#ccc' : '#666' }}>
                                {cmd.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={cmd.label}
                                primaryTypographyProps={{
                                    sx: {
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: isDark ? '#e0e0e0' : '#1f2d3d'
                                    }
                                }}
                            />
                            {cmd.shortcut && (
                                <Chip
                                    label={cmd.shortcut}
                                    size="small"
                                    sx={{
                                        height: 20,
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        backgroundColor: isDark ? '#333' : '#f0f0f0',
                                        color: isDark ? '#bbb' : '#666'
                                    }}
                                />
                            )}
                        </ListItemButton>
                    ))
                )}
            </List>

            <Box sx={{
                borderTop: `1px solid ${isDark ? '#333' : '#eee'}`,
                px: 2, py: 1,
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '11px',
                color: isDark ? '#888' : '#999'
            }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <span>↑↓ to navigate</span>
                    <span>↵ to select</span>
                    <span>esc to close</span>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                        label={navigator.platform.toLowerCase().includes('mac') ? '⌘K' : 'Ctrl+K'}
                        size="small"
                        sx={{
                            height: 18,
                            fontSize: '10px',
                            fontWeight: 700,
                            backgroundColor: isDark ? '#2a2a2a' : '#f5f5f5',
                            color: isDark ? '#bbb' : '#666',
                            border: `1px solid ${isDark ? '#444' : '#ddd'}`
                        }}
                    />
                </Box>
            </Box>
        </Dialog>
    );
};

export default CommandPalette;
